import React, { useEffect, useMemo, useState } from 'react'
import { MeetingTemplatePreset } from '../../../../../store/ai/meetingTemplate'
import { Col, Container, Row } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { ResultCard } from './ResultCard/ResultCard'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectSelectedMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { selectAiFeatures } from '../../../../../store/meetingSummary/aiFeatures'
import { selectAiResultPollingEnabled, selectAiResults, startAiResultPolling, stopAiResultPolling } from '../../../../../store/meetingSummary/aiResults'
import {
  selectExecutedAiFeatures,
  selectNonExecutedPresetsForTemplate,
  selectPollingManuallyExecutedAiFeatureId,
  setExecutionLoading,
  setPollingManuallyExecutedAiFeatureId,
  startExecutedAiFeaturesPolling,
  stopExecutedAiFeaturesPolling,
} from '../../../../../store/meetingSummary/executedAiFeatures'
import { MEETING_TEMPLATE_COLUMNS } from '../../../../../views/Setup/MeetingTemplates/utils'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomAndAIFeaturesTrackingDetails, getRoomTrackingDetails } from '../../tracking'
import { selectRoomSession, selectRoomSessionHasTranscription } from '../../../../../store/meetingSummary/roomSession'
import { useExecuteAiFeatures } from '../hooks/api'
import classnames from 'classnames'

const getMeetingTemplatePresetMetadata = (preset: MeetingTemplatePreset) => {
  try {
    return JSON.parse(preset.metadata) ?? {}
  } catch {
    return {}
  }
}

export const MeetingResults: React.FC = () => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const aiFeatures = useAppSelector(selectAiFeatures)
  const aiResults = useAppSelector(selectAiResults)
  const executedAiFeatures = useAppSelector(selectExecutedAiFeatures)
  const areAiResultsPolling = useAppSelector(selectAiResultPollingEnabled)
  const roomSession = useAppSelector(selectRoomSession)
  const nonExecutedPresets = useAppSelector(selectNonExecutedPresetsForTemplate)

  const [shouldOpenAll, setShouldOpenAll] = useState<boolean>(false)

  useEffect(() => {
    if (!executedAiFeatures.length) {
      dispatch(startExecutedAiFeaturesPolling())
    }
  }, [dispatch, executedAiFeatures])

  useEffect(() => {
    const executedAiFeatureIdsWithInputParameters = executedAiFeatures
      .filter((aiFeatureInfo) => aiFeatures.some((aiFeature) => aiFeature.id === aiFeatureInfo.id))
      .map((aiFeatureInfo) => ({ aiFeatureId: aiFeatureInfo.id, inputParametersHierarchyId: aiFeatureInfo.inputParametersHierarchyId ?? null }))

    if (!executedAiFeatureIdsWithInputParameters.length) {
      return
    }

    const resultAiFeatureIdsWithInputParameters = aiResults.map((aiResult) => ({
      aiFeatureId: aiResult.aiFeatureId,
      inputParametersHierarchyId: aiResult.inputParametersHierarchyId ?? null,
    }))

    const needResult = executedAiFeatureIdsWithInputParameters.some((executedAiFeature) => {
      return !resultAiFeatureIdsWithInputParameters.some(({ aiFeatureId, inputParametersHierarchyId }) => {
        const { aiFeatureId: executedAiFeatureId, inputParametersHierarchyId: executedInputParametersHierarchyId } = executedAiFeature
        return executedAiFeatureId === aiFeatureId && executedInputParametersHierarchyId === inputParametersHierarchyId
      })
    })
    if (needResult && !areAiResultsPolling) {
      dispatch(startAiResultPolling())
    } else if (!needResult && areAiResultsPolling) {
      dispatch(stopAiResultPolling())
      dispatch(stopExecutedAiFeaturesPolling())
    }
  }, [dispatch, executedAiFeatures, aiFeatures, aiResults, areAiResultsPolling])

  const meetingTemplatePresets = useMemo(
    () =>
      !selectedMeetingTemplate
        ? []
        : selectedMeetingTemplate.presets.map((preset, index) => {
            const { aiFeaturePresetId, aiFeatureId } = preset
            const { row = index, column = 'A', colSpan = 1 } = getMeetingTemplatePresetMetadata(preset)
            const columnIndex = MEETING_TEMPLATE_COLUMNS.indexOf(column)
            return {
              aiFeaturePresetId,
              aiFeatureId,
              row,
              column: columnIndex === -1 ? 0 : columnIndex,
              colSpan,
            }
          }),
    [selectedMeetingTemplate],
  )

  const columns = useMemo(() => {
    return meetingTemplatePresets.length ? Math.max(...meetingTemplatePresets.map(({ column, colSpan }) => column + colSpan)) : 0
  }, [meetingTemplatePresets])
  const rows = useMemo(() => {
    return meetingTemplatePresets.length ? Math.max(...meetingTemplatePresets.map(({ row }) => row)) + 1 : 0
  }, [meetingTemplatePresets])

  const columnIndexes = useMemo(() => Array.from(Array(columns).keys()), [columns])
  const rowIndexes = useMemo(() => Array.from(Array(rows).keys()), [rows])

  useEffect(() => {
    if (shouldOpenAll) {
      setShouldOpenAll(false)
    }
  }, [shouldOpenAll])

  if (!selectedMeetingTemplate) {
    return null
  }

  const _openAllResults = () => {
    setShouldOpenAll(true)
  }

  const openAllResults = tracked(_openAllResults, {
    eventName: TrackEvent.AI_RESULT_OPENED,
    eventDetails: {
      ...getRoomTrackingDetails(roomSession),
      openMethod: 'all',
    },
  })

  return (
    <Container className='w-100 mw-100 p-0'>
      <Row className='p-0'>
        <Col className='d-flex justify-content-between p-0 align-items-center'>
          <h5>{selectedMeetingTemplate.name}</h5>
          <div className='d-flex gap-2 ml-auto align-items-center'>
            <OpenAllButton onClick={openAllResults} />
          </div>
        </Col>
      </Row>
      <Row className='mt-4'>{!nonExecutedPresets.length ? null : <ExecuteAllBanner />}</Row>
      {rowIndexes.map((row) => {
        return (
          <Row key={`${selectedMeetingTemplate.id}-${row}`} className='mt-0 gy-4 gx-0 gx-lg-4'>
            {columnIndexes.map((column) => {
              const meetingTemplatePreset = meetingTemplatePresets.find(({ row: presetRow, column: presetColumn }) => {
                return presetRow === row && presetColumn === column
              })
              if (!meetingTemplatePreset) {
                const needsEmptySpace = meetingTemplatePresets
                  .filter(({ row: presetRow, colSpan }) => presetRow === row && colSpan !== 1)
                  .map(({ column: presetColumn, colSpan }) => {
                    return columnIndexes.slice(presetColumn, presetColumn + colSpan)
                  })
                  .every((presetColumns) => !presetColumns.includes(column))
                if (!needsEmptySpace) {
                  return null
                }
                const colStyle = 12 / columns
                return <Col key={`${selectedMeetingTemplate.id}-${row}-${column}-empty`} className='result-column' xs={12} lg={colStyle} />
              }
              const { colSpan, aiFeaturePresetId, aiFeatureId } = meetingTemplatePreset
              const colStyle = (12 / columns) * colSpan
              return (
                <Col key={`${selectedMeetingTemplate.id}-${aiFeaturePresetId}-${aiFeatureId}`} className='result-column' xs={12} lg={colStyle}>
                  <ResultCard meetingTemplatePresetView={meetingTemplatePreset} row={row} shouldOpenAll={shouldOpenAll} showBusinessEvents={true} />
                </Col>
              )
            })}
          </Row>
        )
      })}
    </Container>
  )
}

const OpenAllButton: React.FC<{
  onClick: () => void
}> = ({ onClick }) => {
  return (
    <Button variant='header' className='button-small px-2 zocks-py-0.375 zocks-gap-0.375' onClick={onClick}>
      <i className='bi bi-fullscreen' /> Open all
    </Button>
  )
}

const ExecuteAllBanner: React.FC = () => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const executeAiFeatures = useExecuteAiFeatures()
  const roomSession = useAppSelector(selectRoomSession)
  const nonExecutedPresets = useAppSelector(selectNonExecutedPresetsForTemplate)
  const pollingManuallyExecutedAiFeatureId = useAppSelector(selectPollingManuallyExecutedAiFeatureId)
  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)
  const executedAiFeatures = useAppSelector(selectExecutedAiFeatures)

  const [dismissedRunAll, setDismissedRunAll] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  if (!executedAiFeatures.length || dismissedRunAll || !selectedMeetingTemplate || !hasTranscription) {
    return null
  }

  const _handleRunAiFeatures = () => {
    const aiFeatureIds = nonExecutedPresets.map((preset) => preset.aiFeatureId).filter((featureId): featureId is string => !!featureId)
    if (aiFeatureIds.length) {
      setLoading(true)
      dispatch(setExecutionLoading(true))
      executeAiFeatures(aiFeatureIds, selectedMeetingTemplate.id)
      dispatch(setPollingManuallyExecutedAiFeatureId(aiFeatureIds[aiFeatureIds.length - 1]))
      dispatch(startExecutedAiFeaturesPolling())
    }
  }

  const handleRunAiFeatures = tracked(_handleRunAiFeatures, {
    eventName: TrackEvent.AI_FEATURE_MANUAL_RUN,
    eventDetails: getRoomAndAIFeaturesTrackingDetails(
      roomSession,
      nonExecutedPresets.map((feature) => feature.aiFeatureId).filter((featureId): featureId is string => !!featureId),
    ),
  })

  return (
    <div className='zocks-border-0.25 d-flex p-3 align-items-center justify-content-between gap-1 zocks-bg-color-primary-light my-3'>
      <div className='d-flex gap-3 align-items-center'>
        <div className='d-flex gap-3 align-items-center'>
          <i className='bi bi-exclamation-circle' />
          <div className='paragraph-medium'>Do you want to run all the presets?</div>
        </div>
        <ScopedBlock
          scope={{
            scope: 'aifeature:execute',
            superScope: ['aifeature:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Button
            variant='primary'
            className='button-small px-1 zocks-py-0.125'
            onClick={handleRunAiFeatures}
            disabled={!!pollingManuallyExecutedAiFeatureId || loading}
          >
            <i
              className={classnames('bi bi-arrow-repeat', {
                'spin-faster': loading,
              })}
            ></i>
            Run all
          </Button>
        </ScopedBlock>
      </div>
      <div>
        <Button
          variant='borderless'
          className='zocks-bg-color-primary-light p-1'
          onClick={() => {
            setDismissedRunAll(true)
          }}
        >
          <i className='bi bi-x' />
        </Button>
      </div>
    </div>
  )
}
